import {
	alpha,
	AppBar,
	Box,
	Collapse,
	CssBaseline,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Toolbar,
	Typography
} from '@mui/material';
import { Fragment, PropsWithChildren, useEffect, useState } from 'react';
import {
	IconFolder,
	IconManageUsers,
	IconMic,
	IconSeries,
	IconUsers,
	IconVideos,
} from 'shared/_ui/icon';
import { useAtom } from 'jotai';
import { drawerExpandedAtom, drawerOpenAtom } from 'shared/_components/app-layout/store';
import { NavLinkListItem, NavListItem } from 'shared/_components/app-layout/nav/nav-link-list-item';
import { useUserContext } from 'shared/Providers/User';
import RecentMenuItem from 'shared/_components/app-layout/nav/recent-menu-item';
import { r } from 'shared/constants/routes';
import Version from 'shared/components/Version';
import { isWeb } from 'shared/constants/modules';
import { Link } from 'react-router-dom';
import { ROUTE } from 'shared/constants/routes_old';
import { useIsDesktopSmall, useIsMobile } from 'shared/_model/utils/media-query';
import {
	NAV_BAR_DRAWER_RIGHT_SPACE,
	NAV_BAR_DRAWER_RIGHT_SPACE_COLLAPSED,
	NAV_DRAWER_WIDTH
} from 'shared/_components/app-layout/constants';
import Menu from '@mui/icons-material/Menu';
import {
	closedMixin,
	Div,
	NavDrawer,
	NavDrawerDivider,
	NavDrawerHeader,
	NavDrawerInner,
	NavDrawerLogoIcon, openedMixin,
	NAV_COLORS,
} from './styles';

function NavDrawerLogo({ expanded }: { expanded: boolean }): JSX.Element {
	const Wrapper = !isWeb
		? Fragment
		: ({ children }: PropsWithChildren) => <Link to={ROUTE.home}>{children}</Link>;

	return (
		<Wrapper>
			<NavDrawerLogoIcon expanded={expanded} />
		</Wrapper>
	);
}

function NavContent({ expanded }: { expanded: boolean }) {
	const [manageUsersExpanded, setManageUsersExpanded] = useState(false);
	const {
		permissions: { canUseApp, canReadUsers },
		canSeeExperiments,
	} = useUserContext();

	return (
		<Div
			// sx={{ width: 250 }}
			role="presentation"
			sx={(theme) => ({
				transform: 'scaleX(-1)', // Move scroll to the left
				overflowX: 'hidden',
				overflowY: 'auto',
				height: '100%',
				display: 'flex',
				justifyContent: 'flex-end',
				width: NAV_DRAWER_WIDTH,
				...(expanded && {
					...openedMixin(theme),
				}),
				...(!expanded && {
					...closedMixin(theme),
				}),
				'&::-webkit-scrollbar-thumb': {
					background: alpha(theme.palette.common.white, 0.25),
				},
				'&::-webkit-scrollbar-track': {
					background: NAV_COLORS.bgHover,
				},
			})}
			// onClick={handleDrawerClose}
			// onKeyDown={handleDrawerClose}
		>
			<Div sx={(theme) => ({
				transform: 'scaleX(-1)',
				minHeight: '100%',
				height: 'max-content',
				transition: theme.transitions.create('width', {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.leavingScreen,
				}),
				display: 'flex',
				width: `calc(100% - ${
					expanded ? NAV_BAR_DRAWER_RIGHT_SPACE : NAV_BAR_DRAWER_RIGHT_SPACE_COLLAPSED
				}px)`,
				backgroundColor: NAV_COLORS.bg,
			})}>
				{/* <Box sx={{ transform: 'scaleX(-1)' }}> */}
				<NavDrawerInner expanded={expanded}>
					<NavDrawerHeader expanded={expanded}>
						<NavDrawerLogo expanded={expanded} />
					</NavDrawerHeader>
					<List sx={{ flex: 1, py: 0, backgroundColor: NAV_COLORS.bg }}>
						<NavLinkListItem
							key="projects"
							expanded={expanded}
							icon={<IconFolder />}
							text="PROJECTS"
							to={r.projects.path}
						/>
						<NavLinkListItem
							key="series"
							expanded={expanded}
							icon={<IconSeries />}
							text="SERIES"
							to={r.series.path}
						/>
						<NavLinkListItem
							key="videos"
							expanded={expanded}
							icon={<IconVideos />}
							text="VIDEOS"
							to={r.videos.path}
						/>
						<NavLinkListItem
							key="audios"
							expanded={expanded}
							icon={<IconMic />}
							text="AUDIO"
							to={r.audios.path}
						/>
						<NavDrawerDivider />
						{canUseApp && canReadUsers && (
							<NavLinkListItem
								key="members"
								expanded={expanded}
								icon={<IconUsers />}
								text="MEMBERS"
								to={r.members.path}
							/>
						)}
						{canUseApp && canReadUsers && (
							<>
								<NavListItem
									key="manage_users"
									expanded={expanded}
									icon={<IconManageUsers />}
									text="MANAGE USERS"
									onClick={() => setManageUsersExpanded((s) => !s)}
									isGroupExpanded={manageUsersExpanded}
								/>
								<Collapse in={manageUsersExpanded} timeout="auto" unmountOnExit>
									<List component="div" disablePadding>
										<NavLinkListItem
											level={1}
											key="users"
											expanded={expanded}
											text="USERS"
											to={r.users.path}
										/>
										<NavLinkListItem
											level={1}
											key="roles"
											expanded={expanded}
											text="ROLES"
											to={r.roles.path}
										/>
										<NavLinkListItem
											level={1}
											key="communications"
											expanded={expanded}
											text="COMMS"
											to={r.communications.path}
										/>
										<NavLinkListItem
											level={1}
											key="stats"
											expanded={expanded}
											text="STATS"
											to={r.stats.path}
										/>
									</List>
								</Collapse>
							</>
						)}
						<RecentMenuItem drawerExpanded={expanded} />
						{canSeeExperiments && (
							<>
								<NavDrawerDivider />
								<NavLinkListItem
									key="experiments"
									expanded={expanded}
									text="EXPERIMENTS"
									to={r.experiments.path}
								/>
							</>
						)}
					</List>
					<Version />
				</NavDrawerInner>
				{/* </Box> */}
			</Div>
		</Div>
		// </>
	);
}

export default function Nav() {
	const [drawerExpanded, setDrawerExpanded] = useAtom(drawerExpandedAtom);
	const [mobileOpen, setMobileOpen] = useAtom(drawerOpenAtom);
	const isMobileOpen = useIsMobile() && mobileOpen;

	// const [mobileOpen, setMobileOpen] = useState(false);
	const [isClosing, setIsClosing] = useState(false);

	const handleDrawerClose = () => {
		setIsClosing(true);
		setMobileOpen(false);
	};

	const handleDrawerTransitionEnd = () => {
		setIsClosing(false);
	};

	// const handleDrawerToggle = () => {
	// 	if (!isClosing) {
	// 		setMobileOpen(!mobileOpen);
	// 	}
	// };

	// Remove this const when copying and pasting into your project.
	const container = window !== undefined ? () => window.document.body : undefined;

	const isSmallDesktop = useIsDesktopSmall();
	useEffect(() => {
		setDrawerExpanded(!isSmallDesktop);
	}, [isSmallDesktop, setDrawerExpanded]);

	console.log('tst: NAV', { drawerExpanded, mobileOpen: isMobileOpen });
	return (
		<Box sx={{ display: 'flex' }}>
			{/* <Box */}
			{/*	component="nav" */}
			{/*	sx={{ width: { sm: NAV_DRAWER_WIDTH }, flexShrink: { sm: 0 } }} */}
			{/*	aria-label="mailbox folders" */}
			{/* > */}
			{/* The implementation can be swapped with js to avoid SEO duplication of links. */}
			<NavDrawer
				container={container}
				variant="temporary"
				open={isMobileOpen}
				expanded
				onTransitionEnd={handleDrawerTransitionEnd}
				onClose={handleDrawerClose}
				ModalProps={{
					keepMounted: true, // Better open performance on mobile.
				}}
				sx={{
					display: { xs: 'block', sm: 'none' },
					'& .MuiDrawer-paper': {
						boxSizing: 'border-box',
						// width: NAV_DRAWER_WIDTH,
					},
				}}
			>
				<NavContent expanded={drawerExpanded || isMobileOpen} />
			</NavDrawer>
			<NavDrawer
				variant="permanent"
				expanded={drawerExpanded}
				sx={{
					display: { xs: 'none', sm: 'block' },
					'& .MuiDrawer-paper': {
						boxSizing: 'border-box',
						// width: NAV_DRAWER_WIDTH,
					},
				}}
				open
			>
				<NavContent expanded={drawerExpanded || isMobileOpen} />
			</NavDrawer>
			{/* </Box> */}
		</Box>
	);
}

export function Nav_old() {
	const [drawerExpanded, setDrawerExpanded] = useAtom(drawerExpandedAtom);
	const [manageUsersExpanded, setManageUsersExpanded] = useState(false);
	const {
		permissions: { canUseApp, canReadUsers },
		canSeeExperiments,
	} = useUserContext();

	const isSmallDesktop = useIsDesktopSmall();

	useEffect(() => {
		setDrawerExpanded(!isSmallDesktop);
	}, [isSmallDesktop, setDrawerExpanded]);

	return (
		<NavDrawer variant="permanent" open expanded={drawerExpanded} anchor="left">
			<NavDrawerInner expanded={drawerExpanded}>
				<NavDrawerHeader expanded={drawerExpanded}>
					<NavDrawerLogo expanded={drawerExpanded} />
				</NavDrawerHeader>
				<List sx={{ flex: 1 }}>
					<NavLinkListItem
						key="projects"
						expanded={drawerExpanded}
						icon={<IconFolder />}
						text="PROJECTS"
						to={r.projects.path}
					/>
					<NavLinkListItem
						key="series"
						expanded={drawerExpanded}
						icon={<IconSeries />}
						text="SERIES"
						to={r.series.path}
					/>
					<NavLinkListItem
						key="videos"
						expanded={drawerExpanded}
						icon={<IconVideos />}
						text="VIDEOS"
						to={r.videos.path}
					/>
					<NavLinkListItem
						key="audios"
						expanded={drawerExpanded}
						icon={<IconMic />}
						text="AUDIO"
						to={r.audios.path}
					/>
					<NavDrawerDivider />
					{canUseApp && canReadUsers && (
						<NavLinkListItem
							key="members"
							expanded={drawerExpanded}
							icon={<IconUsers />}
							text="MEMBERS"
							to={r.members.path}
						/>
					)}
					{canUseApp && canReadUsers && (
						<>
							<NavListItem
								key="manage_users"
								expanded={drawerExpanded}
								icon={<IconManageUsers />}
								text="MANAGE USERS"
								onClick={() => setManageUsersExpanded((s) => !s)}
								isGroupExpanded={manageUsersExpanded}
							/>
							<Collapse in={manageUsersExpanded} timeout="auto" unmountOnExit>
								<List component="div" disablePadding>
									<NavLinkListItem
										level={1}
										key="users"
										expanded={drawerExpanded}
										text="USERS"
										to={r.users.path}
									/>
									<NavLinkListItem
										level={1}
										key="roles"
										expanded={drawerExpanded}
										text="ROLES"
										to={r.roles.path}
									/>
									<NavLinkListItem
										level={1}
										key="communications"
										expanded={drawerExpanded}
										text="COMMS"
										to={r.communications.path}
									/>
									<NavLinkListItem
										level={1}
										key="stats"
										expanded={drawerExpanded}
										text="STATS"
										to={r.stats.path}
									/>
								</List>
							</Collapse>
						</>
					)}
					<RecentMenuItem drawerExpanded={drawerExpanded} />
					{canSeeExperiments && (
						<>
							<NavDrawerDivider />
							<NavLinkListItem
								key="experiments"
								expanded={drawerExpanded}
								text="EXPERIMENTS"
								to={r.experiments.path}
							/>
						</>
					)}
				</List>
				<Version />
			</NavDrawerInner>
		</NavDrawer>
	);
}
